import { flexStyle, rgba } from '@themes/mixins';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import Icon from '../Icon';
import Loading from '../Loading';

import { Sizes, StyleTypes } from './enums';
import { Props } from './types';

export const StyledLoader = styled(Loading)``;
export const Root = styled.button<Partial<Props> & { $hasLabel: boolean }>`
  ${flexStyle({ align: 'center', justify: 'center' })}
  cursor: pointer;
  border-radius: 6px;
  border: 1px;
  transition: background-color 200ms ease-in-out;
  width: ${({ width100 }) => (width100 ? '100%' : 'initial')};

  &:disabled {
    cursor: not-allowed;
  }

  ${({ styleType, theme }) => {
    switch (styleType) {
      case StyleTypes.outlined:
        return css`
          background-color: transparent;
          color: ${theme.colors.primary};
          border: 1px solid ${theme.colors.primary};

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.primary};
            border-bottom-color: ${({ theme }) => theme.colors.primary};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.primary, 6)};
          }

          &:disabled {
            border: 1px solid ${theme.colors.divider};
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.link:
        return css`
          background-color: ${rgba(theme.colors.primaryLight, 6)};
          color: ${theme.colors.primary};
          text-decoration: none;

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.primary};
            border-bottom-color: ${({ theme }) => theme.colors.primary};
          }
          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.primaryLight, 10)};
          }
          &:disabled {
            background-color: transparent;
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.danger:
        return css`
          background-color: transparent;
          color: ${theme.colors.red};
          border: 1px solid ${theme.colors.red};

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.red};
            border-bottom-color: ${({ theme }) => theme.colors.red};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.red, 10)};
          }

          &:disabled {
            background-color: ${theme.colors.surfaceSecondary};
            color: ${theme.colors.grey};
            border: 1px solid ${theme.colors.divider};
          }
        `;
      case StyleTypes.success:
        return css`
          background-color: ${theme.colors.green};
          color: ${theme.colors.white};
          border: 1px solid ${theme.colors.green};

          & ${StyledLoader} {
            border-top-color: ${theme.colors.white};
            border-bottom-color: ${theme.colors.white};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.green, 85)};
          }

          &:disabled {
            background-color: ${theme.colors.surfaceSecondary};
            color: ${theme.colors.grey};
            border: 1px solid ${theme.colors.divider};
          }
        `;
      case StyleTypes.update:
        return css`
          background-color: transparent;
          color: ${theme.colors.turquoiseicon};
          border: 1px solid ${theme.colors.turquoiseicon};

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.turquoiseicon};
            border-bottom-color: ${({ theme }) => theme.colors.turquoiseicon};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.turquoiseicon, 6)};
          }

          &:disabled {
            border: 1px solid ${theme.colors.divider};
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.update_violet:
        return css`
          background-color: transparent;
          color: ${theme.colors.violetIcon};
          border: 1px solid ${theme.colors.violetIcon};

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.violetIcon};
            border-bottom-color: ${({ theme }) => theme.colors.violetIcon};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.violetIcon, 6)};
          }

          &:disabled {
            border: 1px solid ${theme.colors.divider};
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.simple:
        return css`
          background-color: transparent;
          color: ${theme.colors.textPrimary};
          border: none;

          & ${StyledLoader} {
            border-top-color: ${theme.colors.textPrimary};
            border-bottom-color: ${theme.colors.textPrimary};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.divider, 10)};
          }

          &:disabled {
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.simpleDanger:
        return css`
          background-color: transparent;
          color: ${theme.colors.red};
          border: none;
          padding: 0;

          & ${StyledLoader} {
            border-top-color: ${theme.colors.white};
            border-bottom-color: ${theme.colors.white};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.red, 10)};
          }

          &:disabled {
            color: ${theme.colors.grey};
          }
        `;
      case StyleTypes.successOutlined:
        return css`
          background-color: transparent;
          color: ${theme.colors.successOutlinedButton};
          border: 1px solid ${theme.colors.successOutlinedButton};
          padding: 0;

          & ${StyledLoader} {
            border-top-color: ${theme.colors.white};
            border-bottom-color: ${theme.colors.white};
          }

          &:hover:not(:disabled) {
            background-color: ${rgba(theme.colors.successOutlinedButton, 10)};
          }

          &:disabled {
            color: ${theme.colors.grey};
          }
        `;

      default: // filled
        return css`
          background-color: ${theme.colors.primary};
          color: ${theme.colors.white};
          border-color: ${theme.colors.primary};

          & ${StyledLoader} {
            border-top-color: ${({ theme }) => theme.colors.background};
            border-bottom-color: ${({ theme }) => theme.colors.background};
          }

          &:hover:not(:disabled) {
            background-color: ${theme.colors.primaryDark};
          }

          &:disabled {
            background-color: ${theme.colors.surfaceSecondary};
            color: ${theme.colors.grey};
            border: 1px solid ${theme.colors.divider};
          }
        `;
    }
  }}

  ${({ $hasLabel, size, styleType }) => {
    switch (size) {
      case Sizes.small:
        return css`
          padding: ${$hasLabel
            ? styleType === StyleTypes.simple ||
              styleType === StyleTypes.simpleDanger
              ? '0 10px'
              : '0 16px'
            : '0 6px'};
          height: 32px;
        `;
      case Sizes.large:
        return css`
          padding: ${$hasLabel ? '0 18px' : '0 12px'};
          height: 54px;
        `;
      default: // medium
        return css`
          padding: ${$hasLabel ? '0 16px' : '0 10px'};
          height: 40px;
        `;
    }
  }}
`;

export const LeftIcon = styled(Icon)<{
  $buttonDisabled?: boolean;
  $hasLabel: boolean;
}>`
  margin-right: ${({ $hasLabel }) => ($hasLabel ? '8px' : '0')};
  transition: scale 200ms ease-in-out;
  scale: 1;

  ${Root}:hover & {
    scale: 1.1;
  }
`;

export const RightIcon = styled(Icon)<{
  $buttonDisabled?: boolean;
  $hasLabel: boolean;
}>`
  margin-left: ${({ $hasLabel }) => ($hasLabel ? '8px' : '0')};
  transition: scale 200ms ease-in-out;
  scale: 1;

  ${({ $buttonDisabled }) =>
    !$buttonDisabled &&
    css`
      ${Root}:hover & {
        scale: 1.1;
      }
    `}
`;

export const Label = styled.span`
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  line-height: normal;
  letter-spacing: 1.25px;
`;

export const StyledLink = styled(Link)`
  text-decoration: none;
`;
